import React from "react";
import { Link } from "react-router-dom";
import {
    SparklesIcon,
    SignalIcon,
    UsersIcon,
    GiftIcon,
    PlayIcon,
    CloudIcon,
    BellAlertIcon,
    ChatBubbleBottomCenterTextIcon,
    WrenchIcon,
    MegaphoneIcon,
    PhotoIcon,
    ChartBarIcon,
    ClipboardDocumentListIcon,
    ArrowRightIcon,
    BoltIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "@/context/AuthContext";

type IconType = React.ComponentType<React.SVGProps<SVGSVGElement>>;

interface QuickAction {
    label: string;
    href: string;
    icon: IconType;
    description: string;
}

interface CategorySection {
    title: string;
    description: string;
    items: { label: string; href: string; description: string }[];
}

const QUICK_ACTIONS: QuickAction[] = [
    {
        label: "Live sessions",
        href: "/live",
        icon: SignalIcon,
        description: "See who is on the water right now.",
    },
    {
        label: "Find a user",
        href: "/users",
        icon: UsersIcon,
        description: "Search profiles, devices, audit log.",
    },
    {
        label: "Wind alerts",
        href: "/wind-alerts",
        icon: BellAlertIcon,
        description: "Trigger digests + check delivery history.",
    },
    {
        label: "System status",
        href: "/system-status",
        icon: ChartBarIcon,
        description: "Backend health, errors, traces.",
    },
];

const CATEGORIES: CategorySection[] = [
    {
        title: "Operations",
        description: "Day-to-day production work — what's happening now, what's broken, who's on the water.",
        items: [
            { label: "Live", href: "/live", description: "Real-time sessions and active riders" },
            { label: "Users", href: "/users", description: "Find users, edit profiles, view audit history, devices, kites" },
            { label: "System Status", href: "/system-status", description: "Service health, traces, slow endpoints, error patterns" },
            { label: "Admin Log", href: "/admin-log", description: "Audit trail of admin actions across the system" },
        ],
    },
    {
        title: "Wind",
        description: "Wind providers and the wind-alert pipeline.",
        items: [
            { label: "Wind Providers", href: "/wind-providers", description: "Configured upstream forecast sources and their health" },
            { label: "Wind Alerts", href: "/wind-alerts", description: "Trigger digest, view evaluations, delivery analytics" },
        ],
    },
    {
        title: "Competitions",
        description: "Competition management — heats, entrants, devices, and live event control.",
        items: [
            { label: "Competitions", href: "/competitions", description: "List, create, and manage competitions" },
        ],
    },
    {
        title: "Content",
        description: "Editorial content, gear catalogue, ads, and spot photos.",
        items: [
            { label: "Whats New", href: "/whatsnew", description: "Release notes and feature announcements shown in-app" },
            { label: "Gear", href: "/gear", description: "Kite brands, models, versions, gear types" },
            { label: "Advertisements", href: "/advertisements", description: "Campaigns and banners shown to users" },
            { label: "Spot Images", href: "/spot-images", description: "Curate photos shown on spot detail pages" },
        ],
    },
    {
        title: "Subscriptions",
        description: "Free subscription grants for ambassadors, partners, and special cases.",
        items: [
            { label: "Free Subscriptions", href: "/free-subscriptions", description: "Grant, end, and review free Pro subscriptions" },
        ],
    },
];

const WelcomePage: React.FC = () => {
    const { user } = useAuth();
    const firstName = user?.name?.split(" ")[0] || user?.username || "admin";

    return (
        <div className="max-w-6xl mx-auto py-8 sm:py-12">
            {/* Greeting */}
            <div className="mb-10">
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
                    Welcome back, {firstName}
                </h1>
                <p className="mt-2 text-gray-600">
                    The Surfr admin console — operations, content, wind, and the data behind it.
                </p>
            </div>

            {/* Surfr.AI hero */}
            <Link
                to="/chat"
                className="group block mb-12 rounded-2xl border border-cyan-200 bg-gradient-to-br from-cyan-50 via-white to-purple-50 p-6 sm:p-8 hover:border-cyan-300 hover:shadow-md transition-all"
            >
                <div className="flex items-start gap-4">
                    <div className="rounded-xl bg-cyan-600 p-3 text-white shrink-0">
                        <SparklesIcon className="size-7" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Surfr.AI Chat
                            </h2>
                            <span className="text-[10px] font-bold tracking-wider rounded-full bg-cyan-600 text-white px-1.5 py-0.5">
                                BETA
                            </span>
                        </div>
                        <p className="mt-2 text-sm text-gray-700 max-w-2xl">
                            Ask questions in plain English about production data — sessions, users,
                            wind alerts, GA4 analytics, crash reports, slow endpoints, and more.
                            Drop in screenshots or PDFs to ask about them. Voice in, voice out.
                        </p>
                        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500">
                            <span className="inline-flex items-center gap-1">
                                <BoltIcon className="size-3.5" />
                                <kbd className="rounded border border-gray-300 bg-white px-1.5 py-0.5 font-mono text-[10px]">
                                    ⌘K
                                </kbd>
                                from anywhere
                            </span>
                            <span>•</span>
                            <span>26 production-data tools</span>
                            <span>•</span>
                            <span>Admin only</span>
                        </div>
                    </div>
                    <ArrowRightIcon className="size-5 text-gray-400 group-hover:text-cyan-600 group-hover:translate-x-1 transition-all shrink-0" />
                </div>
            </Link>

            {/* Quick actions */}
            <section className="mb-12">
                <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
                    Quick actions
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {QUICK_ACTIONS.map((qa) => (
                        <Link
                            key={qa.href}
                            to={qa.href}
                            className="group rounded-xl border border-gray-200 bg-white p-4 hover:border-gray-300 hover:shadow-sm transition-all"
                        >
                            <qa.icon className="size-5 text-gray-600 group-hover:text-gray-900" />
                            <p className="mt-3 text-sm font-semibold text-gray-900">{qa.label}</p>
                            <p className="mt-1 text-xs text-gray-500">{qa.description}</p>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Categories */}
            <section className="space-y-10">
                <div>
                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                        All sections
                    </h2>
                    <p className="mt-1 text-xs text-gray-500">
                        Everything in the sidebar, grouped by purpose. New here? Start with Operations.
                    </p>
                </div>
                {CATEGORIES.map((cat) => (
                    <CategoryBlock key={cat.title} category={cat} />
                ))}
            </section>

            {/* Footer hint */}
            <div className="mt-16 rounded-xl border border-gray-200 bg-gray-50 p-4 text-center">
                <p className="text-xs text-gray-600">
                    Lost? The sidebar on the left has everything. Hover to expand. Press{" "}
                    <kbd className="rounded border border-gray-300 bg-white px-1.5 py-0.5 font-mono text-[10px]">
                        ⌘K
                    </kbd>{" "}
                    to ask Surfr.AI for help finding things.
                </p>
            </div>
        </div>
    );
};

const CATEGORY_ICONS: Record<string, IconType> = {
    Operations: SignalIcon,
    Wind: CloudIcon,
    Competitions: PlayIcon,
    Content: ClipboardDocumentListIcon,
    Subscriptions: GiftIcon,
};

const ITEM_ICONS: Record<string, IconType> = {
    "/live": SignalIcon,
    "/users": UsersIcon,
    "/system-status": ChartBarIcon,
    "/admin-log": ClipboardDocumentListIcon,
    "/wind-providers": CloudIcon,
    "/wind-alerts": BellAlertIcon,
    "/competitions": PlayIcon,
    "/whatsnew": ChatBubbleBottomCenterTextIcon,
    "/gear": WrenchIcon,
    "/advertisements": MegaphoneIcon,
    "/spot-images": PhotoIcon,
    "/free-subscriptions": GiftIcon,
};

const CategoryBlock: React.FC<{ category: CategorySection }> = ({ category }) => {
    const Icon = CATEGORY_ICONS[category.title];
    return (
        <div>
            <div className="flex items-center gap-2 mb-1">
                {Icon && <Icon className="size-5 text-gray-700" />}
                <h3 className="text-base font-semibold text-gray-900">{category.title}</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">{category.description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {category.items.map((item) => {
                    const ItemIcon = ITEM_ICONS[item.href];
                    return (
                        <Link
                            key={item.href}
                            to={item.href}
                            className="group flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 hover:border-gray-300 hover:shadow-sm transition-all"
                        >
                            {ItemIcon && (
                                <ItemIcon className="size-5 text-gray-500 group-hover:text-gray-900 shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900 group-hover:text-cyan-700">
                                    {item.label}
                                </p>
                                <p className="mt-0.5 text-xs text-gray-500">{item.description}</p>
                            </div>
                            <ArrowRightIcon className="size-4 text-gray-300 group-hover:text-cyan-600 group-hover:translate-x-0.5 transition-all shrink-0 mt-1" />
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};

export default WelcomePage;
